/**
 * Curator Bridge Slice-4 client types.
 *
 * Hand-written on purpose. These describe the JSON the `curate_signal` Edge
 * Function returns, not database rows, and the generated Database type predates
 * the accepted 1W/3W migrations - it knows nothing of publication_state,
 * publication_version, v_signal_curation_queue or signal_publication_decisions.
 * Deriving these from `Tables<...>` would therefore not typecheck, and
 * regenerating types against an environment where the migrations are not
 * installed would reproduce the stale schema. Both are deferred to controlled
 * activation; until then this file is the client's single source of shape.
 *
 * Every vocabulary here mirrors supabase/functions/_shared/curator-decisions.ts.
 * The server remains authoritative: nothing in this file is a permission, and a
 * client that sent a state or kind the server rejects simply receives a 4xx.
 */

export const PUBLICATION_STATES = [
  'pending_review',
  'published',
  'rejected',
] as const;
export type PublicationState = (typeof PUBLICATION_STATES)[number];

export const DECISION_KINDS = [
  'approve',
  'reject',
  'unpublish_for_review',
  'unpublish_reject',
  'reopen',
] as const;
export type DecisionKind = (typeof DECISION_KINDS)[number];

/**
 * The withheld states the queue can list. `published` is never among them: the
 * queue view contains withheld signals only, which is exactly why the published
 * unpublish path needs its own context read.
 */
export const LISTABLE_QUEUE_STATES = ['pending_review', 'rejected'] as const;
export type ListableQueueState = (typeof LISTABLE_QUEUE_STATES)[number];

/**
 * Kinds that withhold or withdraw visibility must carry a reason; restoring
 * visibility may stand alone. Mirrors REASON_REQUIRED_KINDS on the server, which
 * refuses a blank reason at the boundary AND in the database.
 */
export const REASON_REQUIRED_KINDS: readonly DecisionKind[] = [
  'reject',
  'unpublish_for_review',
  'unpublish_reject',
];

export function requiresReason(kind: DecisionKind): boolean {
  return REASON_REQUIRED_KINDS.includes(kind);
}

/**
 * Which decisions a curator may offer from each state, mirroring the accepted
 * server transition matrix. Offering anything else would produce a 409 the user
 * could not have anticipated, so the affordance and the matrix are kept in step.
 */
export const DECISIONS_BY_STATE: Record<
  PublicationState,
  readonly DecisionKind[]
> = {
  pending_review: ['approve', 'reject'],
  rejected: ['approve', 'reopen'],
  published: ['unpublish_for_review', 'unpublish_reject'],
};

export function decisionsForState(
  state: PublicationState,
): readonly DecisionKind[] {
  return DECISIONS_BY_STATE[state];
}

export const DECISION_LABELS: Record<DecisionKind, string> = {
  approve: 'Approve',
  reject: 'Reject',
  unpublish_for_review: 'Unpublish for review',
  unpublish_reject: 'Unpublish and reject',
  reopen: 'Reopen',
};

export const PUBLICATION_STATE_LABELS: Record<PublicationState, string> = {
  pending_review: 'Pending review',
  published: 'Published',
  rejected: 'Rejected',
};

/**
 * The latest decision recorded against a signal, as the queue view projects it.
 * Absent for a signal that has never been decided (a pending_review row from
 * ingestion has no decision yet).
 */
export interface LatestDecision {
  kind: DecisionKind | null;
  reason: string | null;
  actorDisplayName: string | null;
  decidedAt: string | null;
}

/**
 * One withheld signal as the curation queue returns it.
 *
 * This is SIGNAL AND SOURCE CONTEXT, not retained source evidence. Every field
 * here comes from the signals row and its organization join. The retained
 * source_event_evidence record - the raw captured document - is NOT joined by
 * the queue view and is not available to this slice; `sourceEventId` is a
 * lineage identifier, not the evidence itself.
 */
export interface CuratorQueueRow {
  signalId: string;
  publicationState: PublicationState;
  /** Canonical decimal text, never a number: it is a bigint on the server. */
  publicationVersion: string;
  title: string | null;
  summary: string | null;
  organizationName: string | null;
  signalType: string | null;
  sourceSystem: string | null;
  /** Only ever an http/https URL; any other scheme is dropped. */
  sourceUrl: string | null;
  sourceEventId: string | null;
  observedAt: string | null;
  firstObservedAt: string | null;
  lastObservedAt: string | null;
  observedCount: number | null;
  createdAt: string | null;
  whyItMatters: string | null;
  recommendedAction: string | null;
  talkTrack: string | null;
  latestDecision: LatestDecision | null;
  decisionCount: number | null;
}

export interface CuratorQueuePage {
  rows: CuratorQueueRow[];
  publicationState: ListableQueueState;
  nextCursor: string | null;
}

/** The three fields get_publication_context returns, and nothing else. */
export interface PublicationContext {
  signalId: string;
  publicationState: PublicationState;
  publicationVersion: string;
}

/**
 * What a decision returns, read from the accepted envelope's nested `signal`
 * object. Every field is non-nullable: a response that cannot supply them is
 * refused at the boundary rather than rendered as a partially-understood
 * success.
 *
 * `idempotent` marks a replay of the same decision key, in which case the
 * outcome is the ORIGINAL decision's, taken from the stored ledger row - not the
 * signal's current state, which later decisions may have moved on.
 */
export interface DecisionResult {
  signalId: string;
  publicationState: PublicationState;
  /** Canonical decimal text; the server re-states it exactly, never rounded. */
  publicationVersion: string;
  idempotent: boolean;
}
